/**
 * useCalibrationFlags Hook
 * 
 * Handles parsing and providing calibration flags from API response.
 * This is the data layer for calibration logic.
 */

import { useMemo } from 'react';
import { parseCalibrationFlags } from '../utils/calibrationParser';
import { getDataSourceScenario } from '../utils/calibrationParser';
import type { ICalibrationAvailability } from '../types/calibration';

interface UseCalibrationFlagsParams {
  calibrationFlagsFromHook: any;
}

interface UseCalibrationFlagsResult {
  calibrationFlags: ICalibrationAvailability | null;
  scenario: 'mscience' | 'vgi' | 'both' | 'none';
}

/**
 * Hook to parse and manage calibration flags
 * 
 * @param calibrationFlagsFromHook - Raw calibration flags from API response
 * @returns Parsed calibration flags and data source scenario
 * 
 * @example
 * ```typescript
 * const { calibrationFlags, scenario } = useCalibrationFlags({
 *   calibrationFlagsFromHook: apiResponse.calibration
 * });
 * // scenario: 'mscience' | 'vgi' | 'both' | 'none'
 * ```
 */
export function useCalibrationFlags(
  params: UseCalibrationFlagsParams
): UseCalibrationFlagsResult {
  const { calibrationFlagsFromHook } = params;

  const calibrationFlags = useMemo(() => {
    if (!calibrationFlagsFromHook) {
      console.warn('[useCalibrationFlags] No calibration flags provided from hook');
      return null;
    }
    
    return parseCalibrationFlags(calibrationFlagsFromHook);
  }, [calibrationFlagsFromHook]);

  const scenario = useMemo(() => {
    if (!calibrationFlags) {
      // Default to 'both' when no flags (common metrics only)
      return 'both' as const;
    }
    
    return getDataSourceScenario(calibrationFlags);
  }, [calibrationFlags]);

  return { calibrationFlags, scenario };
}
